/**
  * 月度运营费用组件
  * 
  * 版本: 1.1.0
  * 变更记录:
  * - 2025-09-29: 租金合计增加物业费（basicInfo.propertyFee），仅与租金相加，不影响抽成计算
  */
import React, { useEffect, useState, useCallback } from 'react';
import { calculateBrandManagementFee, LaborCostRule, calculateLaborCost, OperatingCostLimits, OperatingCostRatios } from '../utils/dataModel';

interface OperatingCostsProps {
  operatingCosts: {
    waterElectricity: number;
    otherStoreCosts: number;
    brandManagementFee: number;
    laborCosts: number;
    rent: number;
    total: number;
  };
  setOperatingCosts: React.Dispatch<React.SetStateAction<{
    waterElectricity: number;
    otherStoreCosts: number;
    brandManagementFee: number;
    laborCosts: number;
    rent: number;
    total: number;
    waterElectricityManual?: number | null;
  }>>;
  basicInfo: {
    estimatedRevenue: number;
    fixedRent: number;
    rentPercentage: number;
    cityTier?: string;
    propertyFee?: number;
  };
  filteredData?: {
    operatingCostRatios?: Record<string, OperatingCostRatios>;
    operatingCostLimits?: OperatingCostLimits;
    laborCostRules?: LaborCostRule[];
  };
}

// 性能优化：使用React.memo包装组件，避免不必要的重渲染
const OperatingCosts: React.FC<OperatingCostsProps> = React.memo(({
  operatingCosts,
  setOperatingCosts,
  basicInfo,
  filteredData
}) => {
  // 添加折叠状态，默认为折叠
  const [isCollapsed, setIsCollapsed] = useState(true);

  // 水电费的区间限制
  const waterElectricityLimits = filteredData?.operatingCostLimits?.waterElectricity;
  
  // 计算租金合计
  useEffect(() => {
    // 计算租金合计 = 固定租金 + (预估月营业额 * 租金抽成比例)
    const rentValue = basicInfo.fixedRent + (basicInfo.estimatedRevenue * basicInfo.rentPercentage / 100) + (basicInfo.propertyFee || 0);
    
    setOperatingCosts(prev => ({
      ...prev,
      rent: rentValue
    }));
  }, [basicInfo.fixedRent, basicInfo.estimatedRevenue, basicInfo.rentPercentage, basicInfo.propertyFee, setOperatingCosts]);

  // 计算合计
  useEffect(() => {
    const total = 
      operatingCosts.waterElectricity + 
      operatingCosts.otherStoreCosts + 
      operatingCosts.brandManagementFee + 
      operatingCosts.laborCosts + 
      operatingCosts.rent;
    
    setOperatingCosts(prev => ({
      ...prev,
      total
    }));
  }, [
    operatingCosts.waterElectricity,
    operatingCosts.otherStoreCosts,
    operatingCosts.brandManagementFee,
    operatingCosts.laborCosts,
    operatingCosts.rent,
    setOperatingCosts
  ]);

  // 性能优化：使用useCallback缓存水电费变化处理函数
  const handleWaterElectricityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // 空字符串表示清除手动覆盖，立即恢复自动计算值
    if (raw === '') {
      let autoValue = 0;
      // 依据比例计算自动值
      const ratio = filteredData?.operatingCostRatios?.['default']?.waterElectricityRatio || 0;
      if (basicInfo.estimatedRevenue > 0 && ratio) {
        autoValue = Math.round(basicInfo.estimatedRevenue * ratio);
        // 应用上下限
        if (waterElectricityLimits) {
          if (waterElectricityLimits.min !== undefined && autoValue < waterElectricityLimits.min) {
            autoValue = waterElectricityLimits.min;
          } else if (waterElectricityLimits.max !== undefined && autoValue > waterElectricityLimits.max) {
            autoValue = waterElectricityLimits.max;
          }
        }
      }
      setOperatingCosts(prev => ({
        ...prev,
        waterElectricity: autoValue,
        waterElectricityManual: null
      }));
      return;
    }

    // 手动输入不应用上下限，尊重用户值，但不允许负数
    let value = parseFloat(raw);
    if (isNaN(value)) value = 0;
    if (value < 0) value = 0;

    // 更新水电费值，并记录为手动覆盖（不套用上下限）
    setOperatingCosts(prev => ({
      ...prev,
      waterElectricity: value,
      waterElectricityManual: value
    }));
  }, [filteredData, basicInfo.estimatedRevenue, waterElectricityLimits, setOperatingCosts]);

  // 处理门店其他费用变化
  const handleOtherStoreCostsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const otherStoreCosts = parseFloat(e.target.value) || 0;
    setOperatingCosts(prev => ({
      ...prev,
      otherStoreCosts
    }));
  };

  // 处理品牌管理费变化
  const handleBrandManagementFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 用户可以手动输入品牌管理费，但我们不会在此处应用规则
    // 因为规则是基于营业额的，而不是基于用户输入的值
    const brandManagementFee = parseFloat(e.target.value) || 0;
    setOperatingCosts(prev => ({
      ...prev,
      brandManagementFee
    }));
  };

  // 计算品牌管理费（基于当前营业额）
  const calculateBrandFee = useCallback(() => {
    if (basicInfo.estimatedRevenue <= 0 || !filteredData?.operatingCostLimits?.brandManagementFee?.rules) {
      return;
    }

    const brandManagementFee = calculateBrandManagementFee(
      basicInfo.estimatedRevenue,
      filteredData.operatingCostLimits.brandManagementFee.rules
    );

    setOperatingCosts(prev => ({
      ...prev,
      brandManagementFee
    }));
  }, [basicInfo.estimatedRevenue, filteredData?.operatingCostLimits?.brandManagementFee?.rules, setOperatingCosts]);

  // 当营业额变化时，重新计算品牌管理费
  useEffect(() => {
    calculateBrandFee();
  }, [calculateBrandFee]);

  // 处理人工费用变化
  const handleLaborCostsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const laborCosts = Math.round(parseFloat(e.target.value) || 0);
    setOperatingCosts(prev => ({
      ...prev,
      laborCosts
    }));
  };

  // 计算人工费用（基于当前营业额和城市梯队）
  const calculateLabor = useCallback(() => {
    if (basicInfo.estimatedRevenue <= 0 || !basicInfo.cityTier || !filteredData?.laborCostRules?.length) {
      return;
    }

    const laborCosts = calculateLaborCost(
      basicInfo.estimatedRevenue,
      basicInfo.cityTier,
      filteredData.laborCostRules
    );

    setOperatingCosts(prev => ({
      ...prev,
      laborCosts
    }));
  }, [basicInfo.estimatedRevenue, basicInfo.cityTier, filteredData?.laborCostRules, setOperatingCosts]);

  // 当营业额或城市梯队变化时，重新计算人工费用
  useEffect(() => {
    calculateLabor();
  }, [calculateLabor]);

  // 计算各项费用占比
  const calculateRatio = (value: number) => {
    if (!basicInfo.estimatedRevenue) return '0.0%';
    return `${((value / basicInfo.estimatedRevenue) * 100).toFixed(1)}%`;
  };

  // 切换折叠状态
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="mb-2">
      <h2 className="section-title title-operating-costs mb-0 w-full cursor-pointer" onClick={toggleCollapse}>
        月度运营费用<span className="text-xs text-gray-500 ml-1">（可修改）</span>
      </h2>
      
      {!isCollapsed && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
          <div className="form-group mb-1">
            <label htmlFor="waterElectricity" className="form-label mb-0 text-xs">
              水电费（元/月）<span className="text-gray-400"></span>：
              <span className="ml-1 text-xs text-gray-500">
                占比：{calculateRatio(operatingCosts.waterElectricity)}
              </span>
            </label>
            <input 
              type="number" 
              id="waterElectricity" 
              className="form-input py-1 px-2 text-sm"
              value={operatingCosts.waterElectricity || ''}
              onChange={handleWaterElectricityChange}
              min="0"
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="otherStoreCosts" className="form-label mb-0 text-xs">
              门店其他费用（元/月）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{calculateRatio(operatingCosts.otherStoreCosts)}
              </span>
            </label>
            <input 
              type="number" 
              id="otherStoreCosts" 
              className="form-input py-1 px-2 text-sm"
              value={operatingCosts.otherStoreCosts || ''}
              onChange={handleOtherStoreCostsChange}
              min="0"
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="brandManagementFee" className="form-label mb-0 text-xs">
              品牌管理费（元/月）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{calculateRatio(operatingCosts.brandManagementFee)}
              </span>
            </label>
            <input 
              type="number" 
              id="brandManagementFee" 
              className="form-input py-1 px-2 text-sm"
              value={operatingCosts.brandManagementFee || ''}
              onChange={handleBrandManagementFeeChange}
              min="0"
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="laborCosts" className="form-label mb-0 text-xs">
              人工费用（元/月）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{calculateRatio(operatingCosts.laborCosts)}
              </span>
            </label>
            <input 
              type="number" 
              id="laborCosts" 
              className="form-input py-1 px-2 text-sm"
              value={operatingCosts.laborCosts || ''}
              onChange={handleLaborCostsChange}
              min="0"
              step="1"
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="rent" className="form-label mb-0 text-xs">
              租金合计（元/月）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{calculateRatio(operatingCosts.rent)}
              </span>
            </label>
            <input 
              type="number" 
              id="rent" 
              className="form-input py-1 px-2 text-sm"
              value={operatingCosts.rent || ''}
              disabled
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="operatingCostsTotal" className="form-label mb-0 text-xs">
              合计（元/月）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{calculateRatio(operatingCosts.total)}
              </span>
            </label>
            <input 
              type="number" 
              id="operatingCostsTotal" 
              className="form-input py-1 px-2 text-sm font-semibold"
              value={operatingCosts.total || ''}
              disabled
            />
          </div>
        </div>
      )}
    </div>
  );
}); // React.memo结束

export default OperatingCosts;